"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  SimpleGrid,
  Group,
  Button,
  Image,
  Accordion,
  Paper,
  Anchor,
  Table,
} from "@mantine/core";
import Link from "next/link";
import { trackCtaClick } from "@/lib/analytics";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import classes from "./howto.module.css";

// Section counter helper — matches kibbe/[type]/content.tsx SectionHeader
function SectionHeader({
  n,
  label,
  title,
}: {
  n: number;
  label: string;
  title: string;
}) {
  return (
    <Box mb={24}>
      <p className={classes.kicker}>
        N&ordm; {String(n).padStart(2, "0")} &mdash; {label}
      </p>
      <h2 className={classes.sectionTitle}>{title}</h2>
      <hr className={classes.hairline} />
    </Box>
  );
}

const faqs = [
  {
    question: "Can you really do color analysis on yourself?",
    answer:
      "Yes, to a point. The at-home method reliably gets most people to the right season family — warm or cool, deep or light. Where self-analysis struggles is the final variant (Soft Summer vs. Light Summer, say), because you can't see your own face objectively. A second person, repeat sessions, or a photo-based analysis closes that gap.",
  },
  {
    question: "How long does DIY color analysis take?",
    answer:
      "Budget about 45 minutes for setup, the undertone tests, and a proper draping session — plus ideally a second, shorter session on another day to confirm. A professional appointment runs 60–90 minutes and $150–400; a digital analysis takes about 90 seconds.",
  },
  {
    question: "Do I need to buy color drapes?",
    answer:
      "No. Solid-colored clothing, scarves, towels and bedsheets work, as long as you can assemble warm/cool pairs of similar colors — a tomato red and a cherry red tell you more than twenty random shirts.",
  },
  {
    question: "What if I get different answers from different tests?",
    answer:
      "Normal, and usually informative. The wrist and jewelry tests are weak evidence; draping is strong evidence. If the votes split, trust the drape. If the drape itself splits, you're probably neutral-toned, and your value and chroma will define your season more than undertone does.",
  },
  {
    question: "Does dyeing my hair change my season?",
    answer:
      "No — your season comes from skin undertone and eye pattern, which don't change with dye. But dyed hair absolutely changes what reads as harmonious near your face, which is why you wrap it during analysis. Most people look most coherent dyeing within their season's palette.",
  },
  {
    question: "How accurate is at-home color analysis compared to professional?",
    answer:
      "Done carefully — right light, bare face, real draping — most people land in the correct family and get close on the variant. The professional edge is calibrated drapes and a trained outside eye. If you want that check without the appointment, a photo-based AI analysis is the middle ground.",
  },
];

export default function HowToDoColorAnalysisContent({ locale }: { locale: string }) {
  void locale;

  let sectionNumber = 0;
  const nextSection = () => {
    sectionNumber += 1;
    return sectionNumber;
  };

  const linkStyle: React.CSSProperties = {
    color: "inherit",
    textDecoration: "underline",
  };

  return (
    <Container size="lg" py={48}>
      <Stack gap={0}>
        {/* ---------- Hero ---------- */}
        <Box className={classes.hero} mb={56}>
          <Box>
            <p className={classes.heroKicker}>THE AT-HOME METHOD</p>
            <h1 className={classes.heroTitle}>How to Do Color Analysis on Yourself</h1>
            <p className={classes.heroTagline}>
              The full studio method &mdash; undertone, value, chroma, draping &mdash; adapted
              for a mirror, a window, and the clothes you already own.
            </p>

            <div className={classes.heroStats}>
              <div className={classes.heroStat}>
                <p className={classes.heroStatLabel}>Start to finish</p>
                <p className={classes.heroStatValue}>45 min</p>
              </div>
              <div className={classes.heroStat}>
                <p className={classes.heroStatLabel}>In materials</p>
                <p className={classes.heroStatValue}>$0</p>
              </div>
              <div className={classes.heroStat}>
                <p className={classes.heroStatLabel}>Possible seasons</p>
                <p className={classes.heroStatValue}>12</p>
              </div>
            </div>

            <Text fz="sm" c="#6f6a64" mt={20}>
              In a hurry?{" "}
              <Link
                href="/ai-color-analysis"
                style={linkStyle}
                onClick={() => trackCtaClick("howto_ca_early")}
              >
                Our AI analysis reads all of this from two photos in about 90 seconds
              </Link>{" "}
              &mdash; or keep reading and do it the long way.
            </Text>
          </Box>

          <Box className={classes.heroImageWrap}>
            <Image
              src="/how-to-color-analysis/hero.webp"
              alt="Woman testing her color analysis undertone and draping at home near a window"
              fit="cover"
              loading="eager"
              style={{ maxHeight: 480, width: "100%", borderRadius: 4 }}
              fallbackSrc="https://placehold.co/600x800/f5f5f5/666?text=Color+Analysis+Guide"
            />
          </Box>
        </Box>

        {/* ---------- N01 The Framework ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="The Framework" title="What you're actually measuring" />
          <Text className={classes.lede} mb={16}>
            Most guides make color analysis sound like a personality quiz. It isn&apos;t. A
            professional analyst is measuring three physical properties of your coloring, and
            every step of the at-home method exists to estimate one of them.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            <Text fw={700} span>Undertone (hue).</Text>{" "}Whether your skin leans warm (golden,
            peachy) or cool (pink, blue-ish) beneath its surface color. This is not the same as
            how light or dark your skin is &mdash; deep skin can be cool, porcelain skin can be
            warm.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            <Text fw={700} span>Value (depth).</Text>{" "}How light or dark your overall coloring
            reads &mdash; hair, eyes and skin taken together, the way they&apos;d appear in a
            black-and-white photo.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            <Text fw={700} span>Chroma (clarity).</Text>{" "}Whether your coloring is bright and
            saturated or soft and muted. High-chroma people can wear electric colors without
            being swallowed; low-chroma people look better in dusty, greyed-down versions of the
            same hues.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Your season is just the intersection of those three answers. Get the three
            measurements roughly right and the season falls out on its own &mdash; which is why
            this guide walks them in order instead of jumping straight to &quot;are you a
            Summer?&quot;
          </Text>
        </Box>

        {/* ---------- N02 Setup ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Setup" title="Set up an honest mirror" />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
            <div>
              <Text mb={20} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                Bad light produces bad readings &mdash; this step matters more than any test that
                follows. Professional studios use north-facing daylight because it&apos;s the
                most color-neutral light there is. You can get close for free:
              </Text>
              <ul className={classes.numberedList}>
                {[
                  "Sit facing a window in indirect daylight. Not direct sun, not evening light (too warm), and never overhead bathroom lighting (too yellow and casts shadows).",
                  "Bare face. Foundation, bronzer and tinted moisturizer all shift your apparent undertone. Lip balm is fine.",
                  "Pull your hair completely off your face. If your hair is dyed a color far from your natural one, wrap it in a white towel — dyed hair is the single biggest source of wrong self-diagnoses.",
                  "Wear white or drape a white towel over your shoulders. It gives your eye a neutral reference point.",
                  "Gather test fabrics: solid-colored tops, scarves, towels, bedsheets. You need warm vs. cool pairs — a tomato red and a cherry red, a mustard and a lemon, an orange and a fuchsia. Nothing patterned.",
                ].map((item, i) => (
                  <li key={i} className={classes.numberedItem}>
                    <span className={classes.numberFigure}>{String(i + 1).padStart(2, "0")}</span>
                    <span className={classes.numberedItemText}>{item}</span>
                  </li>
                ))}
              </ul>
              <Text mt={20} style={{ lineHeight: 1.6, color: "#6f6a64", fontStyle: "italic" }}>
                Phone cameras auto-correct color, so work in the mirror first and use photos only
                as a second opinion &mdash; with the color filters off.
              </Text>
            </div>
            <Image
              src="/how-to-color-analysis/setup.webp"
              alt="Home color analysis setup: facing a window in indirect daylight with bare face and hair pulled back"
              style={{ aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 4 }}
              loading="lazy"
              fallbackSrc="https://placehold.co/600x450/f5f5f5/666?text=Setup"
            />
          </SimpleGrid>
        </Box>

        {/* ---------- N03 Step One: Undertone ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Step One" title="Find your undertone" />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} mb={28}>
            <Image
              src="/how-to-color-analysis/undertone-veins.webp"
              alt="Inner wrist in daylight showing blue-violet veins — the vein test for cool undertones"
              style={{ aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 4 }}
              loading="lazy"
              fallbackSrc="https://placehold.co/600x450/f5f5f5/666?text=Vein+Test"
            />
            <Image
              src="/how-to-color-analysis/jewelry.webp"
              alt="Gold and silver jewelry compared against bare skin — the jewelry test for warm vs. cool undertone"
              style={{ aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 4 }}
              loading="lazy"
              fallbackSrc="https://placehold.co/600x450/f5f5f5/666?text=Jewelry+Test"
            />
          </SimpleGrid>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Undertone is the hardest property to see directly, so you triangulate it with several
            imperfect tests. No single one is a verdict; you&apos;re collecting votes.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            <Text fw={700} span>The vein test.</Text>{" "}Look at the veins on your inner wrist in
            daylight. Blue or purple suggests cool; green or olive suggests warm; a genuine mix of
            both suggests neutral. This test gets less readable the deeper your skin is &mdash; if
            you can&apos;t call it, skip it rather than force it.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            <Text fw={700} span>The jewelry test.</Text>{" "}Lay a gold chain and a silver chain
            against your bare chest or forearm. One will tend to sit &quot;in&quot; your skin
            while the other sits &quot;on&quot; it like a costume. Gold flattering warm, silver
            flattering cool. Judge the skin around the metal, not whether you like the jewelry
            &mdash; plenty of cool-toned people love gold.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            <Text fw={700} span>The white test.</Text>{" "}Hold pure optic white under your chin, then
            soft cream. Warm undertones usually look washed out against stark white and come alive
            against cream; cool undertones are the reverse.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            <Text fw={700} span>The sun test.</Text>{" "}Think about what your skin does in summer.
            Burn-then-tan or tan-easily leans warm or neutral; burn-and-stay-pink leans cool.
            (This one is weak evidence on its own &mdash; genetics interfere.)
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Tally the votes. Three or four pointing the same way is a real answer. A split vote
            usually means you&apos;re neutral, which is common &mdash; it just means your
            undertone will do less of the deciding and your value and chroma will do more.
          </Text>
        </Box>

        {/* ---------- N04 Step Two: Value ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Step Two" title="Find your value" />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
            <div>
              <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                Take a selfie in your window light and turn it black-and-white. You&apos;re
                asking one question: does your overall coloring read light, medium, or deep?
              </Text>
              <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                Look at hair, brows, eyes and skin as one unit. Pale blonde hair with light eyes
                and fair skin reads light. Black hair with deep brown eyes reads deep. Most people
                land in the middle &mdash; that&apos;s fine, &quot;medium&quot; is a real answer,
                not a failure of the test.
              </Text>
              <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                While the photo is still in black-and-white, note one more thing: the gap between
                your darkest feature and your lightest. Big gap (dark hair, fair skin) means high
                contrast; small gap means low contrast. High-contrast coloring handles bold
                color-blocking and stark combinations; low-contrast coloring looks best when
                colors stay close in depth. Keep this number in your pocket &mdash; it settles ties
                later.
              </Text>
            </div>
            <Image
              src="/how-to-color-analysis/value-contrast.webp"
              alt="Black-and-white portrait used to judge overall value — light, medium or deep coloring"
              style={{ aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 4 }}
              loading="lazy"
              fallbackSrc="https://placehold.co/600x450/f5f5f5/666?text=Value"
            />
          </SimpleGrid>
        </Box>

        {/* ---------- N05 Step Three: Chroma ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Step Three" title="Find your chroma" />
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Chroma is easiest to see in your eyes. In daylight, are they bright and glassy with
            sharply defined color, or soft, smoky, greyed-at-the-edges? Bright eyes usually mean
            bright coloring; muted eyes usually mean muted coloring.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Then confirm with fabric: hold up the most saturated thing you own &mdash; a true
            bright, like cobalt or hot pink &mdash; and then something dusty, like sage or mauve.
            If the bright makes your face pop and the dusty one makes you look faintly tired,
            you&apos;re high-chroma. If the bright wears you &mdash; if you notice the shirt
            before the face &mdash; you&apos;re muted.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            This is the property people most often get wrong about themselves, because we confuse
            what we like with what works. The mirror doesn&apos;t care what&apos;s in your
            wardrobe.
          </Text>
        </Box>

        {/* ---------- N06 Step Four: Draping ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Step Four" title="Drape — the test that actually decides" />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
            <Image
              src="/how-to-color-analysis/draping.webp"
              alt="Holding a test fabric under the chin during a draping session to judge warm vs. cool"
              style={{ aspectRatio: "3 / 4", objectFit: "cover", borderRadius: 4, maxWidth: 400 }}
              loading="lazy"
              fallbackSrc="https://placehold.co/450x600/f5f5f5/666?text=Draping"
            />
            <div>
              <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                Everything so far was estimation. Draping is measurement. One color at a time,
                hold each test fabric under your chin, look at your face &mdash; not the fabric
                &mdash; and give it three seconds.
              </Text>
              <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                A right color does quiet, specific things: skin looks more even, dark circles
                fade, jawline looks a touch sharper, eyes brighter, teeth whiter. A wrong color
                casts shadows around the mouth and chin, pulls out redness or sallowness, and
                makes you notice the fabric instead of the person.
              </Text>
              <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                Work in warm/cool pairs so you&apos;re always comparing, never judging one color in
                a vacuum: tomato red against cherry red, mustard against lemon, olive against
                emerald, orange against fuchsia. Whichever half of each pair wins is a vote for
                warm or cool &mdash; this either confirms your Step One answer or overrules it.
                Trust the drape over the wrist veins every time.
              </Text>
              <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
                Two honest warnings. First, you will be biased toward colors you already wear;
                asking someone blunt to watch is worth more than an hour alone. Second, if every
                color seems to look &quot;fine,&quot; your light is too flat or too warm &mdash;
                fix the light before you conclude you&apos;re a unicorn.
              </Text>
            </div>
          </SimpleGrid>
        </Box>

        {/* ---------- Mid-page CTA (slim) ---------- */}
        <Box mb={56} className={`${classes.ctaBand} ${classes.ctaBandSlim}`}>
          <Text className={classes.ctaHeadline} fz={{ base: 18, md: 20 }}>
            Draping is exactly what our AI does &mdash; it compares dozens of digital drapes
            against your photo and scores the reaction, without the bias.
          </Text>
          <Group gap="sm" wrap="wrap" mt="md">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("howto_ca_mid")}
              size="md"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Try digital draping
            </Button>
          </Group>
        </Box>

        {/* ---------- N07 Your Season ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Your Season" title="Put the three answers together" />
          <Text mb={24} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            You now hold three measurements: undertone, value, chroma. The 12-season system is
            just every useful combination of those, grouped into four families. Find your row:
          </Text>

          <Table
            mb={28}
            withRowBorders
            styles={{
              table: { borderCollapse: "collapse" },
              th: {
                fontFamily: "Fraunces, 'Playfair Display', Georgia, serif",
                fontWeight: 500,
                fontSize: 15,
                color: "#1a1a1a",
                borderBottom: "1px solid #e7e2db",
                textAlign: "left",
                padding: "12px 8px",
              },
              td: {
                borderTop: "1px solid #e7e2db",
                fontSize: 14.5,
                color: "#3a3632",
                padding: "12px 8px",
              },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Your results</Table.Th>
                <Table.Th>Season family</Table.Th>
                <Table.Th>The three variants</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>Warm + light-to-medium + bright</Table.Td>
                <Table.Td>Spring</Table.Td>
                <Table.Td>Light, True, Bright Spring</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Cool + light-to-medium + muted</Table.Td>
                <Table.Td>Summer</Table.Td>
                <Table.Td>Light, True, Soft Summer</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Warm + medium-to-deep + muted</Table.Td>
                <Table.Td>Autumn</Table.Td>
                <Table.Td>Soft, True, Deep Autumn</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>Cool + medium-to-deep + bright</Table.Td>
                <Table.Td>Winter</Table.Td>
                <Table.Td>True, Bright, Deep Winter</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>

          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Then let your strongest characteristic pick the variant. If the most obvious thing
            about you is how light you are, you&apos;re a Light Spring or Light Summer. If
            it&apos;s how muted you are, Soft Summer or Soft Autumn. If it&apos;s depth, the Deep
            seasons; if it&apos;s brightness, the Brights. Neutral undertones almost always land
            in one of the Soft or muted seasons, where undertone matters least.
          </Text>
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            If you&apos;re stuck between two, they&apos;re almost certainly sister seasons sharing
            two of the three properties &mdash; Soft Summer and Soft Autumn, say, split only by
            undertone. Re-drape the pairs that separate them and let your face break the tie. Our{" "}
            <Link href="/seasonal-comparison-tool" style={linkStyle}>
              season comparison tool
            </Link>{" "}
            shows any two palettes side by side.
          </Text>
          <Text style={{ lineHeight: 1.7, color: "#3a3632" }}>
            See every palette laid out in the{" "}
            <Link href="/color-seasons-chart" style={linkStyle}>
              12 season color chart
            </Link>
            .
          </Text>
        </Box>

        {/* ---------- N08 Where It Goes Wrong ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Where It Goes Wrong" title="The five classic mistakes" />
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            After watching a lot of people do this badly, the failure modes are surprisingly
            consistent. Testing in warm evening light or bathroom light &mdash; every reading
            skews warm, and half the internet&apos;s self-diagnosed Autumns are actually Summers
            with yellow bulbs. Analyzing around dyed hair instead of wrapping it. Judging the
            fabric instead of the face &mdash; &quot;I love this green&quot; is wardrobe data, not
            coloring data. Confusing value with undertone &mdash; assuming deep skin must be warm
            or fair skin must be cool, when the two axes are independent. And quitting at the
            family level: &quot;I&apos;m an Autumn&quot; is a start, but the money is in the
            variant, because a Soft Autumn in True Autumn colors still looks slightly off.
          </Text>
          <Text style={{ lineHeight: 1.7, color: "#3a3632" }}>
            One more that deserves its own line: doing all of this once, in one session, tired,
            and treating the result as gospel. Professionals drape twice. Sleep on it, redo Step
            Four on another day, and only trust results that repeat.
          </Text>
        </Box>

        {/* ---------- N09 FAQ ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="FAQ" title="Questions people actually ask" />
          <Accordion variant="separated">
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Control>
                  <Text fw={500} className={classes.serif} fz="lg">
                    {faq.question}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="#6f6a64">
                    {faq.question === "How accurate is at-home color analysis compared to professional?" ? (
                      <>
                        Done carefully &mdash; right light, bare face, real draping &mdash; most
                        people land in the correct family and get close on the variant. The
                        professional edge is calibrated drapes and a trained outside eye. If you
                        want that check without the appointment, a{" "}
                        <Anchor component={Link} href="/ai-color-analysis" fz="sm" c="#6f6a64" td="underline">
                          photo-based AI analysis
                        </Anchor>{" "}
                        is the middle ground.
                      </>
                    ) : (
                      faq.answer
                    )}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* ---------- Bottom CTA ---------- */}
        <Paper className={classes.ctaBand} ta="center" mb={40}>
          <Text
            tt="uppercase"
            fz={11}
            fw={600}
            c="rgba(255,255,255,0.6)"
            style={{ letterSpacing: "0.14em" }}
            mb={8}
          >
            THE SHORTCUT
          </Text>
          <Title order={2} className={classes.ctaHeadline} fz={{ base: 26, md: 34 }}>
            Or let the software squint for you
          </Title>
          <Text className={classes.ctaSubtitle} fz="md" maw={560} mx="auto">
            Everything on this page &mdash; undertone, value, chroma, draping &mdash; is what our
            AI measures from two photos. You get your exact 12-season result, your palette, and
            the colors to avoid, in about 90 seconds.
          </Text>
          <Group justify="center" gap="md" wrap="wrap">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("howto_ca_bottom")}
              size="lg"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Get your color analysis
            </Button>
          </Group>
          <Text fz="sm" mt={16} style={{ color: "rgba(255,255,255,0.7)" }}>
            Prefer a quiz? Start with the{" "}
            <Link href="/color-analysis-quiz" style={{ color: "inherit", textDecoration: "underline" }}>
              free color analysis quiz
            </Link>
            .
          </Text>
        </Paper>

        {/* ---------- Email capture ---------- */}
        <Box mb={40}>
          <EmailResultsCapture
            source="how-to-color-analysis"
            title="Want the printable version?"
            subtitle="Get this guide plus a season-by-season palette cheat sheet in your inbox."
          />
        </Box>

        {/* ---------- Keep Reading ---------- */}
        <Box>
          <p className={classes.kicker}>KEEP READING</p>
          <h3 className={classes.sectionTitle} style={{ fontSize: 22 }}>Go deeper</h3>
          <hr className={classes.hairline} style={{ marginBottom: 20 }} />
          <SimpleGrid cols={{ base: 1, sm: 3, md: 5 }} spacing={12}>
            {[
              { href: "/virtual-color-draping", label: "Draping technique, in detail" },
              { href: "/color-seasons-chart", label: "The 12 seasons, charted" },
              { href: "/undertone-quiz", label: "Undertone quiz" },
              { href: "/contrast-level-and-your-best-colors", label: "Contrast level & your best colors" },
              { href: "/ai-vs-in-person-color-analysis", label: "AI vs. in-person analysis" },
            ].map((l) => (
              <Button
                key={l.href}
                component={Link}
                href={l.href}
                variant="default"
                size="sm"
                styles={{
                  root: { height: "auto", minHeight: 36, padding: "8px 12px" },
                  label: { whiteSpace: "normal", textAlign: "center", lineHeight: 1.3 },
                }}
              >
                {l.label}
              </Button>
            ))}
          </SimpleGrid>
        </Box>
      </Stack>
    </Container>
  );
}
